import { useState, type FormEvent } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import AnalyticsPage from '@/app/analytics/page'
import UnitEconomicsPage from '@/app/analytics/unit-economics/page'
import CustomerPage from '@/app/customers/[customerId]/page'
import FishingPage from '@/app/fishing/page'
import GoldExhibitionSupplierPrototype from '@/app/gold-exhibition/supplier-prototype'
import MissionControlPage from '@/app/page'
import InventoryPage from '@/app/inventory/page'
import OperationsPage from '@/app/operations/page'
import ProductPage from '@/app/inventory/[canonicalVariantId]/page'
import PieceRecoveryPage from '@/app/inventory/recovery/page'
import SettingsPage from '@/app/settings/page'
import AppNavigation from '@/components/AppNavigation'
import { MobileNavigationProvider } from '@/components/MobileNavigationContext'
import ErrorBoundary from '@/components/ErrorBoundary'
import IosPullToRefresh from '@/components/IosPullToRefresh'
import { OperationsBubble } from '@/components/operations/OperationsBubble'
import { useAppAuth } from '@/auth/AuthProvider'
import { STATUSES } from '@/lib/order-helpers'

function AuthPage() {
  const { signIn } = useAppAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      await signIn(email, password)
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Не удалось войти.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bone px-5 py-10">
      <form onSubmit={(event) => void submit(event)} className="w-full max-w-sm rounded-[28px] border border-rule bg-paper p-6 shadow-xl shadow-onyx/5">
        <div className="mb-8">
          <div className="text-[12px] font-semibold uppercase tracking-[0.24em] text-ink-muted">ORI</div>
          <h1 className="mt-3 text-[28px] font-semibold leading-tight text-ink">Вход в админку</h1>
          <p className="mt-2 text-[14px] leading-5 text-ink-muted">Используйте выданную учётную запись.</p>
        </div>
        <label className="block text-[13px] font-medium text-ink-muted">
          Электронная почта
          <input autoFocus autoComplete="username" inputMode="email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} className="mt-2 h-12 w-full rounded-2xl border border-rule bg-white px-4 text-[16px] text-ink outline-none focus:border-ink" />
        </label>
        <label className="mt-4 block text-[13px] font-medium text-ink-muted">
          Пароль
          <input autoComplete="current-password" type="password" required minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} className="mt-2 h-12 w-full rounded-2xl border border-rule bg-white px-4 text-[16px] text-ink outline-none focus:border-ink" />
        </label>
        {error && <div role="alert" className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-[13px] leading-5 text-red-700">{error}</div>}
        <button type="submit" disabled={submitting} className="mt-6 h-12 w-full rounded-2xl bg-onyx text-[15px] font-semibold text-ivory disabled:opacity-60">
          {submitting ? 'Входим…' : 'Войти'}
        </button>
      </form>
    </div>
  )
}

function InventoryPresetRedirect({ view }: { view: 'platforms' }) {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  params.set('view', view)
  return <Navigate to={`/inventory?${params.toString()}`} replace />
}

function OrdersRedirect() {
  const location = useLocation()
  const source = new URLSearchParams(location.search)
  const target = new URLSearchParams()
  target.set('view', 'orders')
  target.set('group', 'order')
  const program = source.get('programType')
  if (program === 'DBS' || program === 'FBS') target.append('orderProgram', program)
  const knownStatuses = new Set(STATUSES.map((status) => status.key))
  const requestedStatuses = source.getAll('status').flatMap((value) => value.split(','))
  for (const value of requestedStatuses) {
    if (knownStatuses.has(value)) target.append('orderStatus', value)
  }
  const city = source.get('city')?.trim()
  if (city) target.set('orderCity', city)
  const orderId = Number(source.get('orderId'))
  if (Number.isSafeInteger(orderId) && orderId > 0) target.set('orderId', String(orderId))
  for (const name of ['q', 'page', 'perPage']) {
    const value = source.get(name)?.trim()
    if (value) target.set(name, value)
  }
  return <Navigate to={`/inventory?${target.toString()}`} replace />
}

export default function App() {
  const location = useLocation()
  const { isLoaded, isSignedIn, hasPortalAccess, localDevelopmentBypass } = useAppAuth()
  const authRoute = location.pathname.startsWith('/sign-in') || location.pathname.startsWith('/sign-up')

  if (!isLoaded) return <div className="grid h-full place-items-center text-sm text-ink-muted">Загрузка…</div>
  if (localDevelopmentBypass && authRoute) return <Navigate to="/" replace />
  if (isSignedIn && authRoute) return <Navigate to="/" replace />
  if (!isSignedIn && !localDevelopmentBypass && !authRoute) return <Navigate to="/sign-in" replace />
  if (isSignedIn && !hasPortalAccess) return <div className="grid h-full place-items-center text-sm text-ink-muted">Страница не найдена.</div>

  if (authRoute) {
    return <Routes>
      <Route path="/sign-in/*" element={<AuthPage />} />
      <Route path="/sign-up/*" element={<Navigate to="/sign-in" replace />} />
    </Routes>
  }

  return (
    <MobileNavigationProvider>
      <div className="flex h-dvh flex-col overflow-hidden md:flex-row">
        <AppNavigation />
        <IosPullToRefresh />
        <ErrorBoundary>
          <main data-pull-refresh-root className="relative z-10 min-w-0 flex-1 overflow-hidden pt-[calc(52px+env(safe-area-inset-top))] md:pt-0">
            <Routes>
            <Route path="/" element={<MissionControlPage />} />
            <Route path="/orders" element={<OrdersRedirect />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/inventory/protection" element={<Navigate to="/" replace />} />
            <Route path="/inventory/reconciliation" element={<Navigate to="/" replace />} />
            <Route path="/inventory/attribution" element={<Navigate to="/" replace />} />
            <Route path="/inventory/recovery" element={<PieceRecoveryPage />} />
            <Route path="/inventory/:canonicalVariantId" element={<ProductPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/analytics/products" element={<Navigate to="/analytics" replace />} />
            <Route path="/analytics/unit-economics" element={<UnitEconomicsPage />} />
            <Route path="/customers/:customerId" element={<CustomerPage />} />
            <Route path="/fishing" element={<FishingPage />} />
            <Route path="/operations" element={<OperationsPage />} />
            <Route path="/pricing/ymarket" element={<InventoryPresetRedirect view="platforms" />} />
            <Route path="/gold-exhibition/*" element={<GoldExhibitionSupplierPrototype />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/storefronts" element={<InventoryPresetRedirect view="platforms" />} />
            <Route path="/settings/stock-guard" element={<Navigate to="/settings?section=stock-sync" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </ErrorBoundary>
        <ErrorBoundary>
          <OperationsBubble />
        </ErrorBoundary>
      </div>
    </MobileNavigationProvider>
  )
}
